// Klasyfikacja kurierow + metadane brandowe (kolory staloznakowe z design systemu).

export type CourierKey =
  | 'inpost'
  | 'dhl'
  | 'goodspeed'
  | 'kurier'
  | 'cod'
  | 'paczkomat'
  /* UCS nie rozpoznał metody wysyłki — patrz courierOfOrder. NIE jest to przewoźnik,
     tylko sygnał, że zamówienie wymaga konwersji w zakładce Parser panelu UCS. */
  | 'unknown';

export const COURIERS: Record<CourierKey, { label: string; cls: string; color: string }> = {
  inpost: { label: 'InPost', cls: 'cb-inpost', color: 'var(--c-inpost)' },
  dhl: { label: 'DHL', cls: 'cb-dhl', color: 'var(--c-dhl)' },
  goodspeed: { label: 'GoodSpeed', cls: 'cb-goodspeed', color: 'var(--c-goodspeed)' },
  kurier: { label: 'Kurier', cls: 'cb-kurier', color: 'var(--c-kurier)' },
  cod: { label: 'COD', cls: 'cb-cod', color: 'var(--c-cod)' },
  paczkomat: { label: 'Paczkomat', cls: 'cb-paczkomat', color: 'var(--c-paczkomat)' },
  unknown: { label: 'Nierozpoznany', cls: 'cb-unknown', color: 'var(--warning)' },
};

type AnyOrder = {
  tags?: string;
  shipping_method?: string;
  delivery_info?: { carrier?: string; type?: string; cod?: boolean };
  // Kurier rozpoznany przez UCS (kolejka pakowania zwraca {pluginId, serviceId, title}).
  // Autorytatywne zrodlo kuriera — patrz courierOfOrder.
  carrier?: { pluginId?: string; serviceId?: string; title?: string | null } | null;
  // Powód z UCS; `identified` = można pakować. Patrz isUnidentifiedOrder.
  reason?: string;
};

export function isGoodspeedOrder(o: AnyOrder): boolean {
  return !!o?.tags && o.tags.toLowerCase().includes('goodspeed');
}
export function isDHLOrder(o: AnyOrder): boolean {
  return (
    o?.delivery_info?.carrier === 'DHL' ||
    (o?.delivery_info?.type === 'courier' &&
      !o?.delivery_info?.cod &&
      (o?.shipping_method || '').toLowerCase().includes('dhl'))
  );
}
export function isCourierOrder(o: AnyOrder): boolean {
  return (
    o?.delivery_info?.type === 'courier' &&
    !o?.delivery_info?.cod &&
    !(o?.shipping_method || '').toLowerCase().includes('dhl')
  );
}
export function isCodOrder(o: AnyOrder): boolean {
  return !!o?.delivery_info?.cod;
}
export function isLockerOrder(o: AnyOrder): boolean {
  return o?.delivery_info?.type === 'locker' || o?.delivery_info?.type === 'locker_weekend';
}

export function courierOf(o: AnyOrder): CourierKey {
  if (isGoodspeedOrder(o)) return 'goodspeed';
  if (isCodOrder(o)) return 'cod';
  if (isLockerOrder(o)) return 'paczkomat';
  if (isDHLOrder(o)) return 'dhl';
  if (isCourierOrder(o)) return 'kurier';
  const sm = (o?.shipping_method || '').toLowerCase();
  if (sm.includes('dhl')) return 'dhl';
  if (sm.includes('inpost') || sm.includes('paczkomat')) return 'inpost';
  if (o?.delivery_info?.carrier === 'InPost') return 'inpost';
  // Nic nie wskazało przewoźnika. Wcześniej stało tu `return 'inpost'` i to jest źródło buga
  // „wszystko żółty INPOST": lista i mapa w panelu mapują pakiet UCS bez tagów Shopify, więc
  // KAŻDE zamówienie bez rozpoznanego kuriera dostawało plakietkę cudzej marki. Operator
  // widział przewoźnika, którego nikt nie wybrał. Lepiej powiedzieć wprost, że nie wiemy.
  return 'unknown';
}

/** Kurier -> nazwa API do druku etykiety. `unknown` nie ma API — zwraca null. */
export function carrierApiName(c: CourierKey): 'inpost' | 'dhl' | 'goodspeed' | null {
  if (c === 'inpost' || c === 'paczkomat') return 'inpost';
  if (c === 'dhl' || c === 'kurier') return 'dhl';
  if (c === 'goodspeed') return 'goodspeed';
  return null;
}

/* UCS zwraca carrier jako nieprzezroczyste {pluginId, serviceId, title} —
   wyświetlamy title; mapowanie na badge jest czysto wizualne. */
export function courierOfTitle(title: string): CourierKey {
  const t = (title || '').toLowerCase();
  if (t.includes('goodspeed') || t.includes('🚀') || t.includes('ekspresowa')) return 'goodspeed';
  if (t.includes('dhl')) return 'dhl';
  if (t.includes('paczkomat') || t.includes('locker')) return 'paczkomat';
  if (t.includes('inpost')) return 'inpost';
  if (t.includes('pobranie') || t.includes('cod')) return 'cod';
  // Puste/nieznane title = brak informacji, nie „jakiś kurier".
  if (!t.trim()) return 'unknown';
  return 'kurier';
}

/* Kurier zamowienia — UCS jest zrodlem prawdy. Kolejka pakowania zwraca `carrier`
   ({pluginId, serviceId, title}) rozpoznany z metody dostawy; preferujemy go (title,
   a w razie braku pluginId). Dopiero przy braku danych z UCS spadamy na heurystyke po
   polach Shopify (courierOf). Lista i mapa zamowien w panelu mapuja pakiet UCS bez tagow
   Shopify, wiec samo courierOf zawsze zwracalo domyslny 'inpost' — stad „zhardkodowany InPost". */
export function courierOfOrder(o: AnyOrder): CourierKey {
  const c = o?.carrier;
  if (c && (c.title || c.pluginId)) return courierOfTitle(c.title || c.pluginId || '');
  return courierOf(o);
}

/**
 * Czy UCS NIE rozpoznał metody wysyłki tego zamówienia.
 *
 * `reason` pochodzi z UCS (`identified` | `no_shipping_line` | `unknown_service_code`) i jest
 * autorytatywne — zamówienie skonwertowane w zakładce Parser wraca jako `identified`, więc
 * odblokowuje się samo. Gdy `reason` nie dojechało (starszy payload), spadamy na brak
 * rozpoznanego kuriera.
 *
 * Takiej paczki NIE WOLNO pakować: UCS odmówi wystawienia etykiety (422
 * rate_not_identifiable), więc operator zmarnowałby czas na pakowanie przesyłki, której nie
 * da się wysłać. Konwersję robi administrator w panelu UCS.
 */
export function isUnidentifiedOrder(o: AnyOrder & { reason?: string }): boolean {
  if (typeof o?.reason === 'string') return o.reason !== 'identified';
  return courierOfOrder(o) === 'unknown';
}

// --- Atrybuty Shopify: data dostawy (_BirdChimeSlotId / Delivery Date) ---
type AnyAttrOrder = {
  customAttributes?: { key: string; value: string }[];
  note_attributes?: { name: string; value: string }[];
  attributes?: { name?: string; key?: string; value: string }[];
};

export function orderAttributes(o: AnyAttrOrder): { key: string; value: string }[] {
  const fromNote = (o.note_attributes || []).map((a) => ({ key: a.name, value: a.value }));
  const fromAttrs = (o.attributes || []).map((a: any) => ({ key: a.name || a.key, value: a.value }));
  return [...(o.customAttributes || []), ...fromNote, ...fromAttrs].filter((a) => a.key);
}

export function extractDeliveryDate(o: AnyAttrOrder): Date | null {
  const attrs = orderAttributes(o);
  const bird = attrs.find((a) => a.key === '_BirdChimeSlotId');
  if (bird?.value) {
    const m = bird.value.match(/D=([0-9]{4}-[0-9]{2}-[0-9]{2})/);
    if (m) {
      const d = new Date(`${m[1]}T00:00:00`);
      if (!isNaN(d.getTime())) return d;
    }
  }
  const del = attrs.find((a) => a.key === 'Delivery Date');
  if (del?.value) {
    const d = new Date(del.value.trim());
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

// Zamowienie jest "istotne", jezeli data dostawy <= jutro (brak daty -> istotne).
export function isRelevantOrder(o: AnyAttrOrder): boolean {
  const d = extractDeliveryDate(o);
  if (!d) return true;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(23, 59, 59, 999);
  return d <= tomorrow;
}

export function formatDeliverySlot(o: AnyAttrOrder): string {
  const d = extractDeliveryDate(o);
  if (!d) return '—';
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
}

// --- format pomocniczy ---
export function fmtDuration(startISO?: string, endISO?: string): string {
  if (!startISO) return '—';
  const start = new Date(startISO).getTime();
  const end = endISO ? new Date(endISO).getTime() : Date.now();
  const diff = Math.max(0, Math.floor((end - start) / 1000));
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function fmtMoney(value?: string | number, currency?: string): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(n)) return String(value);
  return `${n.toFixed(2).replace('.', ',')} ${currency === 'PLN' || !currency ? 'zł' : currency}`;
}

export function initialsOf(name?: string | null): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '—';
}
